import { useEffect, useState } from "react";
import axios from "axios";
import { ClipboardText, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { NavTabs } from "@/components/NavTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Oct 2026, user's explicit ask ("Remove: Mark Discrepancy button
// (allow user to GRN short Qty against the Invoice full Qty and price
// variance too. Add: pop window for enter the remark and build a log
// for the all varience case Qty & Price with full details") - every
// qty/price variance ever acknowledged via GrnApprovalPage's Variance
// Remark popup, newest first. Visible to any user who can approve
// GRNs (user's explicit choice, same "vendor_goods_receipt" page
// permission as the GRN Approval screen itself) - no admin-only gate.
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return format(parseISO(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso;
  }
};

const fmtNum = (n) => {
  if (n === null || n === undefined) return "-";
  // Oct 2026, testing_agent iter341 finding - always 2 decimals (not
  // "10" vs "12.50" depending on whether the number happened to be a
  // whole number) so every rate/qty in this log reads consistently.
  return Number(n).toFixed(2);
};

export default function GrnVarianceLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/grn/variance-log`);
        setLogs(data.logs || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not load the Variance Log");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F2F4F7] font-sans" data-testid="grn-variance-log-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ClipboardText size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">GRN Variance Log</span>
          </div>
        </div>
        <NavTabs />
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-5 py-5">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardText size={22} className="text-[#0E7C86]" />
          <h1 className="text-xl font-heading font-bold text-[#101828]" data-testid="grn-variance-log-title">GRN Variance Log</h1>
        </div>
        <p className="text-sm text-[#475467] mb-4">
          Every Quantity or Price variance acknowledged on a GRN (via "Record Variance &amp; Continue"), with the full
          Ship Qty/Actual Qty and PO Rate/Invoice Rate breakdown and the remark entered at the time.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-[#475467] text-sm" data-testid="grn-variance-log-loading">
            <CircleNotch size={16} className="animate-spin" /> Loading...
          </div>
        )}
        {error && (
          <div className="text-sm text-[#B91C1C] bg-[#E02424]/5 border border-[#E02424]/30 rounded-sm px-3 py-2" data-testid="grn-variance-log-error">
            <WarningCircle size={14} className="inline mr-1" /> {error}
          </div>
        )}
        {!loading && !error && logs.length === 0 && (
          <div className="text-sm text-[#475467] bg-white border border-[#D0D5DD] rounded-sm px-3 py-6 text-center" data-testid="grn-variance-log-empty">
            No variance has been logged yet.
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="grn-variance-log-table">
            <table className="border-collapse w-full text-xs">
              <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading tracking-wide">
                <tr>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Date</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Shipment</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Vendor</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">PO(s)</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Qty Variance</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Price Variance</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Remark</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Submitted By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={`${log.doc_code}-${log.submitted_at}-${i}`} className="bg-white odd:bg-[#F9FAFB] align-top" data-testid={`grn-variance-log-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(log.submitted_at)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#004B87]">{log.doc_code}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{log.company_name || "-"} <span className="text-[#667085]">({log.vendor_code || "-"})</span></td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{(log.po_numbers || []).join(", ") || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {(log.qty_items || []).length === 0 ? (
                        <span className="text-[#98A2B3]">-</span>
                      ) : (
                        <ul className="space-y-1" data-testid={`grn-variance-log-qty-items-${i}`}>
                          {log.qty_items.map((it, j) => (
                            <li key={j} className="whitespace-nowrap">
                              <span className="font-data font-semibold">{it.product_id || it.item_number}</span>{" "}
                              Ship {fmtNum(it.ship_qty)} &rarr; Actual {fmtNum(it.actual_qty)}{" "}
                              <span className={it.qty_variance < 0 ? "text-[#B42318] font-semibold" : "text-[#027A48] font-semibold"}>
                                ({it.qty_variance > 0 ? "+" : ""}{fmtNum(it.qty_variance)} {it.unit_of_measure})
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {(log.price_items || []).length === 0 ? (
                        <span className="text-[#98A2B3]">-</span>
                      ) : (
                        <ul className="space-y-1" data-testid={`grn-variance-log-price-items-${i}`}>
                          {log.price_items.map((it, j) => (
                            <li key={j} className="whitespace-nowrap">
                              <span className="font-data font-semibold">{it.product_id || it.item_number}</span>{" "}
                              PO {fmtNum(it.po_rate)} &rarr; Invoice {fmtNum(it.invoice_rate)}{" "}
                              <span className={it.price_variance < 0 ? "text-[#027A48] font-semibold" : "text-[#B42318] font-semibold"}>
                                ({it.price_variance > 0 ? "+" : ""}{fmtNum(it.price_variance)} {it.currency || ""})
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 max-w-[280px]">{log.remark}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">{log.submitted_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
